// @tennis-matches/core/constants/trainingsDesk
// Kanonische TrainingsDesk-URLs (Produktmarke). Intern bleibt appKey/route „trainerstunden“.

/** Kanonisches erstes URL-Segment */
export const TRAININGS_DESK_CANONICAL_SEGMENT = "trainingsdesk";

/** Kanonischer Pfad-Präfix (ohne trailing slash) */
export const TRAININGS_DESK_PATH_PREFIX = "/trainingsdesk";

/** Hub-Landing für TrainingsDesk */
export const TRAININGS_DESK_HUB_PATH = "/hub/trainingsdesk";

/**
 * Erstes URL-Segment → internes Route-Segment (weiterhin „trainerstunden“).
 * @type {Readonly<Record<string, string>>}
 */
export const TRAININGS_DESK_URL_SEGMENT_ALIASES = Object.freeze({
  trainingsdesk: "trainerstunden",
  trainerstunden: "trainerstunden",
  coachdesk: "trainerstunden",
});

/** Hub-Subpfade, die auf die TrainingsDesk-App zeigen */
export const TRAININGS_DESK_HUB_SUB_SEGMENTS = Object.freeze([
  "trainingsdesk",
  "trainerstunden",
  "coachdesk",
]);

/** Interne SPA-Route für öffentliche Einladungsannahme (ohne Login). */
export const TRAININGS_DESK_PUBLIC_INVITE_ROUTE = "trainingsdesk-invite";

/** URL-Zweitsegmente für Einladungsannahme (Kontakt, Trainer, Kind). */
export const TRAININGS_DESK_INVITE_SUB_SEGMENTS = Object.freeze([
  "einladung",
  "trainer-einladung",
  "invite",
]);

/**
 * @param {string} segment
 * @returns {boolean}
 */
export function isTrainingsDeskInviteSubSegment(segment) {
  const s = String(segment || "").toLowerCase();
  return TRAININGS_DESK_INVITE_SUB_SEGMENTS.includes(s);
}

/**
 * Öffentliche Einladungs-URL (z. B. /trainingsdesk/einladung?token=…).
 * @param {string} pathname
 * @returns {boolean}
 */
export function isTrainingsDeskInvitePathname(pathname) {
  const suffix = getTrainingsDeskPathSuffix(pathname);
  if (suffix == null) return false;
  const first = String(suffix).replace(/^\/+/, "").split("/")[0];
  return isTrainingsDeskInviteSubSegment(first);
}

/**
 * @param {string} segment
 * @returns {string}
 */
export function normalizeTrainingsDeskUrlSegment(segment) {
  const s = String(segment || "").toLowerCase();
  return TRAININGS_DESK_URL_SEGMENT_ALIASES[s] || s;
}

/**
 * @param {string} suffix — optionaler Restpfad ohne führenden Slash
 * @returns {string}
 */
export function buildTrainingsDeskPath(suffix = "") {
  const rest = String(suffix || "").replace(/^\/+/, "");
  return rest
    ? `${TRAININGS_DESK_PATH_PREFIX}/${rest}`
    : TRAININGS_DESK_PATH_PREFIX;
}

/**
 * @param {string} pathname
 * @returns {boolean}
 */
export function isTrainingsDeskPathname(pathname) {
  const p = String(pathname || "").split("?")[0];
  if (
    p === TRAININGS_DESK_PATH_PREFIX ||
    p.startsWith(`${TRAININGS_DESK_PATH_PREFIX}/`)
  )
    return true;
  if (p === "/trainerstunden" || p.startsWith("/trainerstunden/")) return true;
  if (p === "/coachdesk" || p.startsWith("/coachdesk/")) return true;
  if (p === "/hub/trainerstunden" || p.startsWith("/hub/trainerstunden/"))
    return true;
  if (
    p === TRAININGS_DESK_HUB_PATH ||
    p.startsWith(`${TRAININGS_DESK_HUB_PATH}/`)
  )
    return true;
  return false;
}

/**
 * Kanonischen Pfad-Präfix für Navigation erzeugen (immer `/trainingsdesk`).
 * Legacy-Pfade werden beim Schreiben normalisiert.
 *
 * @param {string} suffix
 * @returns {string}
 */
/**
 * Relativer Pfad innerhalb TrainingsDesk (z. B. `/planung`) — unabhängig vom URL-Alias-Präfix.
 * Gibt `""` für Hub-Root, `null` wenn kein TrainingsDesk-Pfad.
 *
 * @param {string} pathname
 * @returns {string|null}
 */
export function getTrainingsDeskPathSuffix(pathname) {
  const p =
    String(pathname || "")
      .split("?")[0]
      .replace(/\/+$/, "") || "/";
  const segs = p.replace(/^\/+/, "").split("/").filter(Boolean);
  if (!segs.length) return null;

  if (segs[0] === "hub" && TRAININGS_DESK_HUB_SUB_SEGMENTS.includes(segs[1])) {
    return segs.length > 2 ? `/${segs.slice(2).join("/")}` : "";
  }

  if (TRAININGS_DESK_URL_SEGMENT_ALIASES[segs[0]] === "trainerstunden") {
    return segs.length > 1 ? `/${segs.slice(1).join("/")}` : "";
  }

  return null;
}
